import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Card, Col, Row, Segmented, Statistic, Typography } from 'antd'
import DealerInterestTable from './DealerInterestTable'
import { RAIL_LABELS, skipReasonLabel } from './labels'
import { getRequest, dutyFollowUpReportUrl } from '../../../config'

const { Text } = Typography
const WINDOWS = [7, 30, 90]

const SkipList = ({ skipped }) => {
  const entries = Object.entries(skipped || {}).filter(([, n]) => n > 0)
  if (!entries.length) return <Text type="secondary">None skipped.</Text>
  // Several server reasons share one plain label ("Switched off"); add them up.
  const merged = entries.reduce((acc, [reason, n]) => {
    const label = skipReasonLabel(reason)
    acc[label] = (acc[label] || 0) + n
    return acc
  }, {})
  return (
    <div>
      {Object.entries(merged).map(([label, n]) => (
        <div key={label}>
          <Text type="secondary">
            {label}: {n}
          </Text>
        </div>
      ))}
    </div>
  )
}

SkipList.propTypes = {
  skipped: PropTypes.object
}

// Did the follow-ups work? Every figure sits beside the baseline it has to beat (spec:
// duty-follow-up-messages, w-admin Req 3).
const ResultsPanel = () => {
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState(null)

  useEffect(() => {
    const load = async () => {
      const response = await getRequest(
        `${dutyFollowUpReportUrl}?days=${days}`,
        'follow-up results',
        setLoading
      )
      if (response.success) setReport(response.data)
    }
    load()
  }, [days])

  const after = report?.afterDelivery
  const invite = report?.dealerInvite
  const drop = report?.dropoff
  const tracking = report?.trackingInvite

  return (
    <div>
      <Segmented
        options={WINDOWS.map(d => ({ label: `Last ${d} days`, value: d }))}
        value={days}
        onChange={setDays}
        style={{ marginBottom: 16 }}
      />
      <Row gutter={16}>
        <Col xs={24} lg={8}>
          <Card title="After delivery" loading={loading && !report}>
            {after && (
              <>
                <Statistic title="Sent" value={after.sent} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {after.sentEarly} as a reply to a &quot;thank you&quot; ·{' '}
                  {after.byVariant.consumer} customers · {after.byVariant.dealer} dealers ·{' '}
                  {after.byVariant.dealer_invite} invitations
                </Text>
                <Statistic
                  style={{ marginTop: 12 }}
                  title="Paid again within 30 days"
                  value={after.paidAgainPct}
                  suffix="%"
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Before these messages, {after.baselinePct}% of customers paid more than once.
                </Text>
                <div style={{ marginTop: 12 }}>
                  <SkipList skipped={after.skipped} />
                </div>
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Dealer invitation" loading={loading && !report}>
            {invite && (
              <>
                <Statistic title="Invited" value={invite.sent} />
                <Row gutter={8} style={{ marginTop: 12 }}>
                  <Col span={8}>
                    <Statistic title="Yes" value={invite.yes} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="No" value={invite.no} />
                  </Col>
                  <Col span={8}>
                    <Statistic title="No answer" value={invite.unanswered} />
                  </Col>
                </Row>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {invite.nowRegistered} of the yes answers are registered dealers now.
                </Text>
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Payment drop-off" loading={loading && !report}>
            {drop && (
              <>
                <Statistic title="Sent" value={drop.sent} />
                <Statistic
                  style={{ marginTop: 12 }}
                  title="Paid within 24 hours"
                  value={drop.paidWithin24hPct}
                  suffix="%"
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Before this message, {drop.baselinePct}% of drop-offs ever paid.
                  {Object.keys(drop.paidByRail || {}).length > 0 &&
                    ` Paid by: ${Object.entries(drop.paidByRail)
                      .map(([rail, n]) => `${RAIL_LABELS[rail] || rail} ${n}`)
                      .join(', ')}.`}
                </Text>
                <div style={{ marginTop: 12 }}>
                  <SkipList skipped={drop.skipped} />
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* The tracking invitation's own row, because its four numbers are a funnel rather than a
          rate: sent → tapped → bill of lading sent → paid. Almost nobody has ever asked this
          number about tracking, so this is the measurement as much as the message — and a tap
          with no document after it is a different finding from no taps at all. */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24}>
          <Card title="Tracking invitation" loading={loading && !report}>
            {tracking && (
              <>
                <Row gutter={8}>
                  <Col xs={12} lg={6}>
                    <Statistic title="Sent" value={tracking.sent} />
                  </Col>
                  <Col xs={12} lg={6}>
                    <Statistic
                      title="Tapped"
                      value={tracking.tapped}
                      suffix={`(${tracking.tappedPct}%)`}
                    />
                  </Col>
                  <Col xs={12} lg={6}>
                    <Statistic title="Sent a bill of lading" value={tracking.billOfLadingSent} />
                  </Col>
                  <Col xs={12} lg={6}>
                    <Statistic
                      title="Paid for tracking"
                      value={tracking.paid}
                      suffix={`(${tracking.paidPct}%)`}
                    />
                  </Col>
                </Row>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {tracking.scheduled} are still waiting for their fortnight.
                  {tracking.failed ? ` ${tracking.failed} could not be sent.` : ''}
                </Text>
                <div style={{ marginTop: 12 }}>
                  <SkipList skipped={tracking.skipped} />
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>

      <DealerInterestTable />
    </div>
  )
}

export default ResultsPanel
